<script lang="ts">
  import { computeUnitWorkflow } from '../../workflow';
  import { workspaceMode } from '../../workspaceMode';
  import { router } from '$lib/router';
  import { unitsQuery } from '../../queries/units';
  import { zonesQuery } from '../../queries/zones';
  import { roomsQuery } from '../../queries/rooms';
  import { loadsQuery } from '../../queries/loads';
  import { inputDevicesQuery } from '../../queries/input_devices';
  import { rcusQuery } from '../../queries/rcus';
  import { dinModulesForUnitQuery } from '../../queries/din_modules';
  import { rcuChannelsQuery } from '../../queries/rcu_channels';
  import { scenesQuery } from '../../queries/scenes';
  import { unitSceneLoadsQuery } from '../../queries/scene_loads';
  import { productsQuery } from '../../queries/products';
  import { dinModuleChannelCount } from '../../hardware';

  const units = unitsQuery();
  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const devices = inputDevicesQuery();
  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();
  const rcuChannels = rcuChannelsQuery();
  const scenes = scenesQuery();
  const sceneLoads = unitSceneLoadsQuery();
  const products = productsQuery();

  let unit = $derived((units.data ?? []).find((u) => u.id === router.unitId) ?? null);
  let unitZones = $derived(zones.data ?? []);
  let unitRooms = $derived(rooms.data ?? []);
  let unitLoads = $derived(loads.data ?? []);
  let unitDevices = $derived(devices.data ?? []);
  let unitInputKeys = $derived((devices.data ?? []).flatMap((d) => d.keys ?? []));
  let unitChannels = $derived(
    (rcuChannels.data ?? []).map((c) => ({ load: c.load, object_id: c.rcu }))
  );

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: unitZones,
      rooms: unitRooms,
      loads: unitLoads,
      inputDevices: unitDevices,
      inputKeys: unitInputKeys,
      rcus: rcus.data ?? [],
      dinModules: dinModules.data ?? [],
      channels: unitChannels,
      scenes: scenes.data ?? [],
      sceneLoads: sceneLoads.data ?? [],
      products: products.data ?? []
    })
  );

  let unitRcus = $derived(unit ? (rcus.data ?? []).filter((rcu) => rcu.unit === unit.id) : []);
  let unitRcuIds = $derived(new Set(unitRcus.map((rcu) => rcu.id)));
  let unitDinModules = $derived((dinModules.data ?? []).filter((din) => unitRcuIds.has(din.rcu)));

  let assignedChannelCount = $derived(
    workflow.entities.channels.filter((c) => unitLoads.some((load) => load.id === c.load)).length
  );

  let totalChannelCapacity = $derived(
    unitRcus.reduce((sum, rcu) => {
      const dinCap = unitDinModules
        .filter((m) => m.rcu === rcu.id)
        .reduce((acc, m) => acc + dinModuleChannelCount(m.module_type, m.dali_bus_count ?? 1), 0);
      return sum + rcu.channel_count + dinCap;
    }, 0)
  );

  let totalKeypads = $derived(unitDevices.length);
  let programmedKeypads = $derived(totalKeypads - workflow.entities.unprogrammedDevices.length);

  let validationLabel = $derived(
    workflow.attentionCount === 0
      ? '✓ Ready'
      : `⚠ ${workflow.attentionCount} issue${workflow.attentionCount !== 1 ? 's' : ''}`
  );

  let validationClass = $derived(
    workflow.attentionCount === 0 ? 'text-state-ok' : 'text-state-warn'
  );

  let modeLabel = $derived(workspaceMode === 'commissioning' ? 'Commissioning' : 'Design');

  function openIssues() {
    document.dispatchEvent(new CustomEvent('expand-rail-issues'));
  }

  function openExportMenu() {
    document.dispatchEvent(new CustomEvent('open-export-menu'));
  }
</script>

<footer
  class="h-6 shrink-0 bg-bg-rail border-t border-border-default flex items-center text-xs text-text-secondary select-none overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
  aria-label="Workspace status"
>
  <button
    type="button"
    class="h-full px-3 shrink-0 border-r border-border-default hover:bg-bg-hover transition-colors duration-100 {validationClass}"
    onclick={openIssues}
  >
    {validationLabel}
  </button>

  <span class="h-full px-3 shrink-0 border-r border-border-default flex items-center font-mono">
    {assignedChannelCount}/{totalChannelCapacity} channels assigned
  </span>

  <span class="h-full px-3 shrink-0 border-r border-border-default flex items-center">
    {programmedKeypads} programmed / {totalKeypads} total
  </span>

  <button
    type="button"
    class="h-full px-3 shrink-0 border-r border-border-default hover:bg-bg-hover transition-colors duration-100"
    onclick={openExportMenu}
  >
    Last firmware: Never exported
  </button>

  <span class="h-full px-3 shrink-0 flex items-center text-2xs uppercase tracking-wide text-text-muted">
    {modeLabel}
  </span>
</footer>
